import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { TrashIcon } from "@heroicons/react/24/outline";
import { FiTruck, FiShoppingCart } from "react-icons/fi";

const cn = (...cls) => cls.filter(Boolean).join(" ");

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const BASE_URL = "https://pro-dash.orima.ma/";

const getItemUnitPrice = (item) => {
  // Priority: saved discounted unit_price -> promo -> selling -> price
  const saved = toNumber(item?.unit_price);
  if (saved > 0) return saved;

  const promo = toNumber(item?.promotional_price);
  if (promo > 0) return promo;

  const sell = toNumber(item?.selling_price);
  if (sell > 0) return sell;

  return toNumber(item?.price);
};

const getImageUrl = (item) => {
  const img = item?.main_image;
  if (!img) return "https://via.placeholder.com/300x400?text=No+Image";
  if (String(img).startsWith("http")) return img;
  return `${BASE_URL}${String(img).replace(/^\/+/, "")}`;
};

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();

  const cartTotal = useMemo(() => {
    const t = toNumber(getCartTotal());
    return t > 0 ? t : 0;
  }, [getCartTotal]);

  const itemsCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + (toNumber(item?.quantity) || 1), 0),
    [cartItems]
  );

  if (!cartItems || cartItems.length === 0) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-white px-4" style={{ fontFamily: "DM Sans, sans-serif" }}>
        <div className="text-center max-w-md">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center text-2xl mb-6">
            🛒
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">Votre panier est vide</h2>
          <p className="text-gray-600 mb-6">Ajoutez un produit pour continuer.</p>

          <Link
            to="/shop"
            className="inline-flex items-center justify-center bg-yellow-200 text-gray-900 px-8 py-4 rounded-2xl font-extrabold hover:bg-yellow-300 transition-transform active:scale-95 btn-attire-yellow"
          >
            Continuer les achats
          </Link>
        </div>

        <style>{`
          @keyframes attractYellow {
            0%, 100% { transform: scale(1); box-shadow: 0 10px 35px rgba(0,0,0,0.12); }
            50% { transform: scale(1.03); box-shadow: 0 18px 55px rgba(0,0,0,0.22); }
          }
          .btn-attire-yellow { animation: attractYellow 1.6s ease-in-out infinite; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "DM Sans, sans-serif" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
          <div>
            <h1 className="text-2xl sm:text-4xl font-extrabold text-gray-900">Votre panier</h1>
            <p className="mt-1 text-sm text-gray-600">{itemsCount} article(s)</p>
          </div>

          <button onClick={clearCart} className="text-sm font-bold text-red-600 hover:text-red-800">
            Vider le panier
          </button>
        </div>

        <div className="grid grid-cols-1 gap-7 lg:grid-cols-12">
          {/* Items */}
          <div className="lg:col-span-7 space-y-5">
            {cartItems.map((item) => {
              if (!item) return null;

              const unit = getItemUnitPrice(item);
              const qty = toNumber(item.quantity) || 1;
              const lineTotal = unit * qty;

              return (
                <div
                  key={item.id}
                  className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm hover:shadow-lg transition-all"
                >
                  <div className="flex gap-4">
                    <Link to={`/product/${item.id}`} className="w-24 h-28 sm:w-28 sm:h-32 flex-shrink-0">
                      <img
                        src={getImageUrl(item)}
                        alt={item.name || "Produit"}
                        className="w-full h-full object-cover rounded-2xl border border-gray-100 bg-gray-50"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/300x400?text=No+Image";
                        }}
                      />
                    </Link>

                    <div className="flex-1 min-w-0">
                      <Link to={`/product/${item.id}`}>
                        <h3 className="text-sm sm:text-base font-extrabold text-gray-900 line-clamp-2">
                          {item.name || "Produit"}
                        </h3>
                      </Link>

                      {item.discount_label && (
                        <div className="mt-1 text-xs font-semibold text-green-700">
                          {item.discount_label}
                        </div>
                      )}

                      <div className="mt-2 text-sm text-gray-700">
                        Unité: <span className="font-extrabold text-gray-900">{unit.toFixed(2)} MAD</span>
                      </div>

                      <div className="mt-1 text-sm text-gray-700">
                        Total: <span className="font-extrabold text-gray-900">{lineTotal.toFixed(2)} MAD</span>
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-3">
                        {/* Qty selector */}
                        <div className="inline-flex items-center gap-3 rounded-full bg-gray-100 px-3 py-2">
                          <button
                            onClick={() => updateQuantity(item.id, Math.max(1, qty - 1))}
                            className="h-9 w-9 rounded-full bg-white border border-gray-200 font-extrabold text-gray-700 hover:bg-gray-50"
                            aria-label="Diminuer quantité"
                          >
                            −
                          </button>

                          <span className="w-8 text-center font-extrabold text-gray-900">{qty}</span>

                          <button
                            onClick={() => updateQuantity(item.id, qty + 1)}
                            className="h-9 w-9 rounded-full bg-white border border-gray-200 font-extrabold text-gray-700 hover:bg-gray-50"
                            aria-label="Augmenter quantité"
                          >
                            +
                          </button>
                        </div>

                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="inline-flex items-center gap-2 text-sm font-bold text-red-600 hover:text-red-800"
                          aria-label="Supprimer"
                        >
                          <TrashIcon className="w-5 h-5" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="pt-2">
              <Link to="/shop" className="text-sm font-bold text-gray-700 hover:text-gray-900">
                ← Continuer vos achats
              </Link>
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-5 lg:sticky lg:top-6 lg:self-start">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm">
              <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 mb-5 text-center">
                Résumé de la commande
              </h2>

              <div className="space-y-3">
                <div className="flex justify-between text-gray-700">
                  <span className="font-semibold">Sous-total</span>
                  <span className="font-extrabold text-gray-900">{cartTotal.toFixed(2)} MAD</span>
                </div>

                <div className="flex justify-between text-gray-700">
                  <span className="font-semibold flex items-center gap-2">
                    <FiTruck className="w-4 h-4" /> Livraison
                  </span>
                  <span className="font-extrabold text-green-600">Gratuite</span>
                </div>

                <div className="pt-3 border-t border-gray-100 flex justify-between items-end">
                  <span className="text-lg font-extrabold text-gray-900">Total</span>
                  <div className="text-right">
                    <div className="text-3xl font-extrabold text-gray-900 leading-none">
                      {cartTotal.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">MAD</div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate("/checkout")}
                className="w-full mt-6 bg-yellow-200 hover:bg-yellow-300 text-gray-900 py-4 rounded-2xl font-extrabold transition-transform active:scale-95 flex items-center justify-center gap-2 btn-attire-yellow"
              >
                <FiShoppingCart className="w-5 h-5" />
                Passer la commande
              </button>

              <div className="mt-5 rounded-2xl bg-rose-500 text-white px-4 py-3 flex items-center justify-between">
                <div className="font-semibold">Livraison gratuite</div>
                <div className="text-xs text-white/90">1 à 2 jours</div>
              </div>

              <div className="mt-4 text-sm text-gray-600 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  Paiement à la livraison.
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  Support disponible.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes attractYellow {
          0%, 100% { transform: scale(1); box-shadow: 0 10px 35px rgba(0,0,0,0.12); }
          50% { transform: scale(1.03); box-shadow: 0 18px 55px rgba(0,0,0,0.22); }
        }
        .btn-attire-yellow { animation: attractYellow 1.6s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default Cart;
